// What each event means. Its metadata is `UIEventMetadata` and its app is
// `UIEventApps`; both are total over this enum, so a new case must declare them.
export enum UIEventType {
  /**
   * Historical: an admin opened a visits-table row's detail panel. One row in
   * eleven months; the table is a card list now, so nothing writes this.
   *
   * It is why `admin_visit_action_tapped` exists: this let the panel be removed
   * on a number, not an argument, so the replacement card counts every action.
   */
  VisitDetailPanelExpanded = 'visit_detail_panel_expanded',
  /**
   * Historical: an admin switched the inquiry list to its now-removed table
   * view. Nothing writes this.
   *
   * It recorded only the table side, so a card choice and an untouched toggle
   * looked the same. That is why a two-way control records both branches.
   */
  InquiryLopezTableSelected = 'inquiry_lopez_table_selected',
  /** An admin opened one of the log screens. */
  AdminLogViewed = 'admin_log_viewed',
  /** Every authed page the admin app renders. */
  AdminPageViewed = 'admin_page_viewed',
  /**
   * Historical: an admin switched a visit dashboard tab, back when tabs were a
   * search param. Tabs are routes now, so `admin_page_viewed` covers them.
   */
  AdminTabSelected = 'admin_tab_selected',
  /**
   * An admin downloaded a spreadsheet (`AdminExportName`). A download leaves no
   * other row. The receipts page is not one: it links to a PDF on S3, and the
   * `receipts` row is its record.
   */
  AdminExportDownloaded = 'admin_export_downloaded',
  /**
   * An admin opened a dialog (`AdminDialogName`). The closed list makes a name
   * with **no rows** a dialog nobody opened, not one nobody instrumented. Fired
   * on mount: a dialog renders only while open.
   */
  AdminDialogOpened = 'admin_dialog_opened',
  /**
   * An admin moved to another tab **inside** a dialog. `AdminDialogOpened`
   * only has the tab it opened **on**, so clicked tabs left no row.
   *
   * A separate event so per-opening counts don't move. Read the two together:
   * the opening is what was asked for, these are the moves after.
   *
   * Fired from `DialogTabs`, so no dialog opts in. Re-clicking the current tab
   * records nothing: Radix fires only on a change.
   */
  AdminDialogTabSelected = 'admin_dialog_tab_selected',
  /**
   * An admin used a table feature (`AdminTableFeature`), keyed by the table's
   * `storageKey`. None reaches the server, so without this an unused filter
   * panel and a daily one look the same. Fired on the decision, never a
   * keystroke.
   */
  AdminTableFeatureUsed = 'admin_table_feature_used',
  /**
   * Which columns a table showed, in screen order. `AdminTableFeatureUsed` says
   * the picker opened; this says what came out, which `localStorage` hides.
   *
   * Recorded on mount and when the list changes, never per keystroke. The full
   * column set lives in code (`getPersonColumnKeys` for persons), so a column
   * that never appears is found by subtraction.
   */
  AdminTableColumnsShown = 'admin_table_columns_shown',
  /**
   * An admin tapped a visit card action (`VisitCardActionName`). Otherwise "does
   * anyone move a visit up" means digging `audit_log` for a change other screens
   * make too.
   *
   * **Group by `action`.** No visit id, so a count is taps. Recorded before the
   * confirms, like `worker_commute_times_requested`: backing out still counts
   * as reaching for it.
   */
  AdminVisitActionTapped = 'admin_visit_action_tapped',
  /**
   * An admin tapped a button in the Visits page's selection pop-up, one of
   * `SelectionActionName`.
   *
   * **Group by `action`.** `count` is how many visits were ticked. Recorded at
   * the tap, before the confirm or dialog, like `admin_visit_action_tapped`.
   */
  AdminSelectionActionTapped = 'admin_selection_action_tapped',
  /**
   * An admin opened the aerial drawing editor, from an `AerialMarkupPlace`.
   * `admin_dialog_opened` can't split the four places; this does.
   *
   * It answers which place is worth keeping. A corrected drawing is an
   * `audit_log` row with no screen on it.
   *
   * Recorded on the tap, not Save: opening it and leaving is still a look.
   */
  AdminAerialMarkupEditOpened = 'admin_aerial_markup_edit_opened',
  /**
   * An admin removed the aerial drawing, from an `AerialMarkupPlace`. Where
   * removes outnumber edits, the quote form's drawing is more often wrong than
   * fixable.
   *
   * On the two immediate-save places the column is already cleared; on the two
   * form places Save must still follow, so a removal can end in no write.
   */
  AdminAerialMarkupRemoved = 'admin_aerial_markup_removed',
  /**
   * An admin changed the satellite zoom in the editor, with `place`, `from`
   * and `to`.
   *
   * It says whether `rezoomStrokes` (keeping the drawing across a zoom) was
   * worth building. The stored `zoom` is only the end state.
   *
   * Recorded on the tap, so a zoom abandoned with Cancel still counts.
   */
  AdminAerialMarkupZoomChanged = 'admin_aerial_markup_zoom_changed',
  /**
   * An admin confirmed a refund on Money > Income > Stripe charges.
   * `admin_dialog_opened` with `refund_payment` is the reach; this is the yes,
   * so the two together are how often a refund is backed out of.
   *
   * Recorded on the tap, before Stripe answers: the payment row is the record
   * of what went through.
   */
  AdminPaymentRefundConfirmed = 'admin_payment_refund_confirmed',
  /**
   * An admin used a control that leaves no other row (`AdminControlName`): a
   * copy button, a call, text or email link, a menu, a show/hide toggle. The
   * closed list makes a name with **no rows** a control nobody used, not one
   * nobody instrumented, as `AdminDialogOpened` does for dialogs.
   *
   * **Group by `control`.** A two-way control carries `value`, the state it was
   * switched to, and records both branches: `inquiry_lopez_table_selected`
   * recorded one side only, so an untouched toggle and the other choice looked
   * the same.
   *
   * Recorded on the tap, never on mount or a keystroke. A menu records its
   * open, not its items: each item ends in a dialog, a page or a write that is
   * counted already.
   */
  AdminControlUsed = 'admin_control_used',
  /**
   * An admin tapped a visit's status icon and its full name opened, with the
   * `status` and the `VisitStatusNamePlace`.
   *
   * It says when the words beside the icons can come off (Roger, 10/8:
   * "Eventually everyone will understand icon and then we remove wording"):
   * few opens per person, the icons are read. **Group by `status`**: one
   * status drawing most opens is the icon to redraw. Taps, once per open.
   */
  AdminVisitStatusNameOpened = 'admin_visit_status_name_opened',
  /** Every authed page the customer app renders. */
  CustomerPageViewed = 'customer_page_viewed',
  /**
   * A customer opened a subscription page but has no saved card or bank: the
   * drop-off we measure.
   */
  CustomerSubscriptionBlockedNoPaymentMethod = 'customer_subscription_blocked_no_payment_method',
  /**
   * A customer opened an inquiry, with `fromLink`: whether via the `?from=link`
   * Roger texts. A link never opened and a link opened with no save are
   * different failures.
   */
  CustomerInquiryOpened = 'customer_inquiry_opened',
  /**
   * A customer saved a change to their inquiry, from a
   * `CustomerInquirySection`. Only this says which section. Recorded on the
   * tap, so a failed save still counts.
   */
  CustomerInquirySaved = 'customer_inquiry_saved',
  /** The customer's twin of `admin_aerial_markup_zoom_changed`. */
  CustomerAerialMarkupZoomChanged = 'customer_aerial_markup_zoom_changed',
  /**
   * A customer tapped Draw under a photo of their snow inquiry. The save is
   * `customer_inquiry_saved` with `photoMarkup`; an open with no save is a
   * pen they gave up on.
   */
  CustomerPhotoMarkupOpened = 'customer_photo_markup_opened',
  /**
   * A customer tapped Download quote (PDF) under Your quote on their snow
   * inquiry. The tap only: the PDF opens off the same link Roger emails, which
   * records nothing, so read it as reach, not as every download.
   */
  CustomerSnowQuotePdfDownloaded = 'customer_snow_quote_pdf_downloaded',
  /**
   * The box under a snow quote with all five prices, asking what price would
   * work, was on the page, with `answered`: whether an answer already stood.
   * One row per mount. The reach the three below are read against.
   */
  CustomerQuotePricesSeen = 'customer_quote_prices_seen',
  /**
   * A customer switched the box between its two ways, `kind`: their own five
   * prices or a percentage lower. Nothing else says which way was looked at
   * before a submit, or by someone who never submitted.
   */
  CustomerQuotePricesOptionChosen = 'customer_quote_prices_option_chosen',
  /**
   * A customer tapped Send in the box, with the `kind` sent. Recorded on the
   * tap, so a refused one counts too; `snow_quote_customer_prices` holds what
   * was saved.
   */
  CustomerQuotePricesSubmitted = 'customer_quote_prices_submitted',
  /** A customer tapped Change under their saved answer to give a new one. */
  CustomerQuotePricesChangeTapped = 'customer_quote_prices_change_tapped',
  /** Every authed page the worker app renders. */
  WorkerPageViewed = 'worker_page_viewed',
  /** Which home-screen item a worker tapped, by its title. */
  WorkerHomeItemTapped = 'worker_home_item_tapped',
  /**
   * A worker picked pictures on the visit page, recorded before anything can
   * eat it. A start with no finish or failure vanished with the phone.
   */
  WorkerPicUploadStarted = 'worker_pic_upload_started',
  /** Every picture of an attempt reached the visit, with the duration. */
  WorkerPicUploadFinished = 'worker_pic_upload_finished',
  /** A picture upload failed: "the app isn't working", made countable. */
  WorkerPicUploadFailed = 'worker_pic_upload_failed',
  /**
   * "Visit Completed" was refused because a crew member's pic was missing. Snow
   * only. The reason is the raw key, never the localized message or names.
   */
  WorkerVisitCompletionBlocked = 'worker_visit_completion_blocked',
  /**
   * A worker tapped "Show commute time", before the confirm and location
   * prompt. Backing out still counts: wanted button, unwanted flow. The times
   * live only in page state, so nothing else records the tap.
   */
  WorkerCommuteTimesRequested = 'worker_commute_times_requested',
  /** The visits page was withheld until the worker finishes an earlier visit. */
  WorkerBlockedByIncompleteVisit = 'worker_blocked_by_incomplete_visit',
  /**
   * A worker backed out of "I'm going to this address" with no pictures: a tap
   * on the wrong visit. The going-to row is deleted and Slack isn't queryable, so
   * only this counts it. Read against `worker_blocked_by_incomplete_visit`; a visit
   * the cancels keep naming sits too close to another on the list.
   */
  WorkerCancelledGoingToVisit = 'worker_cancelled_going_to_visit',
  /**
   * A worker tapped Copy, or Directions and the maps app it opened. The
   * going-to row's flags die with the row at his first picture; this survives.
   * Counting Directions whatever app opens means a Waze driver counts as using
   * directions.
   */
  WorkerAddressButtonTapped = 'worker_address_button_tapped',
  /**
   * A worker's first before-picture came with no address button tapped, so the
   * app nagged him and #worker_job_update. With `worker_address_button_tapped` it
   * gives the share of addresses typed by hand.
   *
   * Written by the server, so it has no `user_agent` or `ip`.
   */
  WorkerAddressButtonNagShown = 'worker_address_button_nag_shown',
  /**
   * A worker submitted his half of a snow day's pay: bags, price, receipts.
   * `worker_submitted_at` says it was submitted; this separates a page never
   * opened from one abandoned. Read against `worker_page_viewed` on `/snow-pay`.
   */
  WorkerPaySubmitted = 'worker_pay_submitted',
  /**
   * A worker changed the app language. `person.worker_app_language` holds the
   * result; this holds that he reached for the picker, and what he left. The
   * audit trigger's old = new skip keeps login's unchanged save out of
   * `audit_log`, so an audit row there is a real switch too.
   *
   * Switches on login or signup aren't recorded: there is no session yet. The
   * column catches those.
   */
  WorkerLanguageSwitched = 'worker_language_switched',
  /**
   * A worker opened the in-app chat. It is a dialog, so this is its only count.
   *
   * **Group by `openedFrom`** (`WorkerChatOpenedFrom`). `toast` judges the
   * sound-and-banner half; `link` counts taps on a texted `?chat=open` link.
   */
  WorkerChatOpened = 'worker_chat_opened',
  /**
   * A worker sent a chat message. A tap with no `worker_chat_message` row is a
   * send that failed on Google, Slack or the network, the likely storm failure
   * nothing else records.
   *
   * **Average `imageCount`.** No text: the `worker_chat_message` row holds it.
   */
  WorkerChatMessageSent = 'worker_chat_message_sent',
  /**
   * A worker tapped *Call or text the office instead* and got the old
   * three-numbers dialog.
   *
   * This count is why `AdminContactDialog` is still here: a winter of
   * near-zero rows is the evidence to delete it. Read against
   * `worker_chat_message_sent`.
   */
  WorkerChatContactsOpened = 'worker_chat_contacts_opened',
  /**
   * A worker tapped Next on a sign-up screen and the form's rules refused it.
   * Otherwise a worker who quits on a screen leaves only a page view.
   * **Group by `fields`**, the errored keys, read per `step`. The same tap
   * posts the errors to #worker_signup in English.
   *
   * Tap only, and a repeat tap with the same fields records nothing.
   */
  WorkerSignupSaveBlocked = 'worker_signup_save_blocked',
  /**
   * A worker tapped a visit's status icon on the visits list and its full name
   * opened, in his language. `admin_visit_status_name_opened`'s twin: few opens
   * per worker, the icons are read and the words can come off. **Group by
   * `status`.** Taps, once per open.
   */
  WorkerVisitStatusNameOpened = 'worker_visit_status_name_opened',
  /**
   * A worker tapped Turn on notifications and his phone's
   * subscription saved, so the office's messages chime on it with the app
   * closed. Recorded only when it took: a refusal or a blocked prompt writes
   * nothing. **Count distinct `person_id`** against the workers who opened the
   * app; `push_subscription` holds only the phones still on.
   */
  WorkerPushSubscribed = 'worker_push_subscribed',
  /**
   * An iPhone in the browser was offered adding the app to its Home Screen,
   * the only place iOS lets it notify. Until 14 Oct 2026 the home page showed
   * the steps, once per home visit; since then the bar under the app bar on
   * every page offers them, recorded once per app load. **Read per
   * `person_id` against `worker_push_subscribed`**: many sightings and no
   * opt-in is a worker the offer lost.
   */
  WorkerPushInstallHintSeen = 'worker_push_install_hint_seen',
  /**
   * A worker tapped Set up on the notifications bar and the steps to add the
   * app to his iPhone's Home Screen opened. **Read per `person_id` between
   * `worker_push_install_hint_seen` and `worker_push_subscribed`**: seen and
   * never opened is a bar he ignores; opened and never subscribed is steps
   * that lost him.
   */
  WorkerPushInstallStepsOpened = 'worker_push_install_steps_opened',
  /**
   * A worker turned notifications off on the Settings page, and the phone's
   * subscription was dropped. **Read against `worker_push_subscribed`**: a
   * worker who turns them off again and again is getting too many.
   */
  WorkerPushUnsubscribed = 'worker_push_unsubscribed',
  /**
   * Roger tapped the quote screen's link to text a quote from his phone. No
   * Twilio, no `sms` row, no #outbound_sms, so this is the only record a quote
   * went out.
   *
   * It means the link opened, not that he pressed send. The same tap records
   * the prices, moves the inquiry to in progress and appends the admin comment,
   * which corroborate it.
   */
  AdminQuoteTextLinkOpened = 'admin_quote_text_link_opened',
  /**
   * An Inquiries search found nothing, so matching people were listed instead.
   * One row per search text, not per render.
   *
   * Answers whether Roger uses the box as a people finder; `phoneShaped` says
   * number or name.
   */
  InquirySearchPeopleFallbackShown = 'inquiry_search_people_fallback_shown',
  /**
   * Roger saved a typed reason in a snow inquiry card's Move to dead dialog.
   * The `inquiry` row holds only the standing move to dead, cleared when the
   * status moves off Dead, and `audit_log` holds the reason and the column
   * change; this row says the dialog was the way in. Count distinct
   * `inquiryId`.
   */
  InquiryMovedToDead = 'inquiry_moved_to_dead',
  /**
   * An admin tapped Save in a snow inquiry card's customer's-price dialog,
   * entering or changing the customer's answer for them. Recorded at the tap,
   * so a refused save is here too; `snow_quote_customer_prices` holds what was
   * saved and the `#inquiry` line says "(entered by admin)". **Group by
   * `kind`**, count distinct `inquiryId`.
   */
  InquiryCustomerQuotePricesSaved = 'inquiry_customer_quote_prices_saved',
  /** A tab hard-reloaded itself because a new deployment shipped. */
  AppAutoRefreshed = 'app_auto_refreshed',
  /**
   * A read the page asked for failed: the network was down (a phone just back
   * in the browser) or the server threw. **Group by `query`**, and split by
   * `refetch`: a failed first load is a screen the person could not use, a
   * failed reload kept what was on screen. `error` tells a dropped connection
   * from a server fault.
   */
  AppQueryFailed = 'app_query_failed',
  /**
   * A visitor tapped Submit on a public quote form and the form's rules refused.
   * Otherwise a stuck customer leaves no trace. **Group by `fields`**, the
   * sorted error keys.
   *
   * It carries the whole form, so a blocked submit with contact details is a
   * lead to call back (`InquirySubmitValues`).
   *
   * Submit tap only: someone mid-typing isn't stuck. The accepted half is
   * `inquiry_submit_validation_passed`.
   */
  InquirySubmitBlocked = 'inquiry_submit_blocked',
  /**
   * A visitor tapped Submit and the form's rules let it through: the denominator
   * `inquiry_submit_blocked` lacked.
   *
   * Fires **at the tap**, before the create action, from the same
   * `handleSubmit`. `inquiry_created` counts taps that became an `inquiry`.
   *
   * It carries the whole form for the blocked half's reason: a failed create
   * leaves no other record of the lead. `submitId` pairs it with its landing.
   */
  InquirySubmitValidationPassed = 'inquiry_submit_validation_passed',
  /**
   * The create action returned an inquiry id: the real lead count. The gap to
   * `inquiry_submit_validation_passed` is what the create action drops.
   *
   * Pair by `submitId`, not by subtracting counts: visitors have no
   * `person_id`. A passed row no created row names is a failed create, still a
   * reachable lead. `inquiryId` joins to the `inquiry` row.
   *
   * Fired beside GA's `generate_lead` on the same line in `useInquirySubmit`.
   * Ad blockers kill GA but not a server action, so this is the floor.
   */
  InquiryCreated = 'inquiry_created',
  /**
   * A visitor tapped Draw under a photo on the snow quote form. What was drawn
   * is on the submit's `values.imageMarkups` and the inquiry's
   * `image_markups`; an open with no drawing there is a pen given up on.
   * Taps, not drawings.
   */
  SnowQuotePhotoMarkupOpened = 'snow_quote_photo_markup_opened',
  /**
   * The photo pen on the snow quote form closed: `closedBy` Done, Cancel or
   * Escape, `strokeCount` what the pen held then (on Done, the strokes kept),
   * and `photoShown` whether the photo ever appeared to draw on. An open with
   * `photoShown` false is a pen the visitor could not draw in; Done does
   * nothing until the photo shows, so that close is always Cancel or Escape.
   * Read beside `snow_quote_photo_markup_opened`: an open with no close is a
   * page left with the pen open.
   */
  SnowQuotePhotoMarkupClosed = 'snow_quote_photo_markup_closed',
  /**
   * A landscaping or handyman visitor whose details mentioned snow tapped the
   * link to the snow site's quote form. `from` is the form or the
   * confirmation. Taps, not snow inquiries: the snow site's own submit is
   * the landing, and nothing joins the two.
   */
  InquirySnowQuoteLinkClicked = 'inquiry_snow_quote_link_clicked',
  /**
   * A quote form opened from the customer app's Start a new inquiry and its
   * link filled at least one field: the name, email and phone, and the
   * Service Address when `withAddress`. Once per page load. Fields a saved
   * draft already held are not filled, so a reload mid-form records nothing.
   * Read against `inquiry_created` on the same site for how many became one.
   */
  InquiryFormPrefilled = 'inquiry_form_prefilled',
}

/**
 * The members marked Historical above: nothing writes them, kept so old rows
 * still mean something. Why each is historical is its comment on the enum.
 * `historicalMemberGate` holds this set and those comments in step.
 */
export const HISTORICAL_UI_EVENT_TYPES: ReadonlySet<UIEventType> = new Set([
  UIEventType.VisitDetailPanelExpanded,
  UIEventType.InquiryLopezTableSelected,
  UIEventType.AdminTabSelected,
]);
